import { AbstractControl, FormGroup } from '@angular/forms';
import { OfferUtils } from '../utils/offer.utils';

/**
 * Validates the length of variants in a control.
 *
 * @param {AbstractControl} control The control to validate.
 *
 * @returns {object|null} Returns an object with the key "invalidVariantLength" set to `true` if the total number of variants is more than 100, otherwise returns `null`.
 */
export function VariantLengthValidator(control: AbstractControl): null | { invalidVariantLength: true } {
    const values: any[][] = Object.values(control.value || {});

    // If we don't have any variants, we don't need to validate
    if (!values || values.length === 0) {
        return null;
    }

    const totalVariants = OfferUtils.countVariants(values);

    // If we have more than 100 variants, we need to invalidate
    if (totalVariants > 100) {
        return { invalidVariantLength: true };
    }

    return null;
}

/**
 * Validates that a form control has only one variant.
 *
 * @param {AbstractControl} control - The control to validate (should be a FormGroup).
 * @returns {object|null} Returns an object with the key "invalidVariantLength" set to `true` if the total number of variants is not 1, otherwise returns `null`.
 */
export function OneVariantValidator(control: AbstractControl): null | { oneVariantOnly: true } {
    // Check if each control has only one variant
    for (let [key, c] of Object.entries((control as FormGroup).controls || {})) {
        const formGroup = control.get(key) as FormGroup;
        const values: any[][] = Object.values(formGroup.value || {});

        if (!values || values.length !== 1) {
            c.setErrors({ oneVariantOnly: true });
            return { oneVariantOnly: true };
        } else {
            // Remove oneVariantOnly error
            const errors = c.errors || {};
            delete errors['oneVariantOnly'];
            c.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
    }

    return null;
}

/**
 * Validates that the SKU values in a form control are unique.
 *
 * @param {AbstractControl} control - The form control to validate.
 * @returns {null | { uniqueSku: true }} Returns null if the SKUs are unique,
 *          otherwise returns an object with the property uniqueSku set to true.
 */
export function ValidateUniqueSku(control: AbstractControl): null {
    const controls = Object.values((control as FormGroup).controls);

    // Get all the SKU values
    const skus: string[] = controls.map((c) => c.get('sku').value);

    // Check if there are any duplicates
    for (const c of controls) {
        const skuControl = c.get('sku');
        const sku = skuControl.value;
        if (skus.filter((s) => s === sku).length > 1) {
            skuControl.setErrors({ uniqueSku: true });
        } else {
            // Remove uniqueSku error
            const errors = c.errors || {};
            delete errors['uniqueSku'];
            skuControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
    }

    return null;
}
